import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ArrowRight,
  ExternalLink,
  FileText,
  Copy,
  Check,
  X,
  ChevronRight,
  Filter,
  Download,
  Award,
  BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/global/SEO';

interface ResearchOverviewProps {
  lang: 'en' | 'ar';
}

interface ResearchPaper {
  id: string;
  titleEn: string;
  titleAr: string;
  category: 'foundation' | 'rag' | 'climate' | 'swarms' | 'safety';
  categoryLabelEn: string;
  categoryLabelAr: string;
  date: string;
  authorsEn: string;
  authorsAr: string;
  abstractEn: string;
  abstractAr: string;
  doi: string;
  researchGateUrl: string;
  pdfUrl: string;
  license: string;
  awardEn?: string;
  awardAr?: string;
  keyFindingsEn: string[];
  keyFindingsAr: string[];
  isSpotlight?: boolean;
}

// Official peer-reviewed and preprint publications by Abdelrahman Abushousha / VEXL Labs
const RESEARCH_PAPERS: ResearchPaper[] = [
  {
    id: 'droughtwatch-ai-v2-multivariate-2026',
    titleEn: 'DroughtWatch AI V2: A Multivariate Bidirectional LSTM Framework for Pixel-Level, Six-Month-Ahead Agricultural Drought Early Warning in the Faiyum Region, Egypt',
    titleAr: 'نظام DroughtWatch AI V2: إطار عمل متعدد المتغيرات لشبكات Bi-LSTM للتنبؤ بالجفاف الزراعي على مستوى البكسل قبل 6 أشهر في إقليم الفيوم، مصر',
    category: 'climate',
    categoryLabelEn: 'Climate & Drought AI',
    categoryLabelAr: 'ذكاء المناخ والجفاف',
    date: 'August 2026',
    authorsEn: 'Abdelrahman M. D. S. A. Abushousha',
    authorsAr: 'عبدالرحمن محمد دياب شوشة (Abdelrahman Abushousha)',
    abstractEn: 'Drought is a slow-onset hazard whose costs are compounded by the short lead time of most operational warning systems. This paper documents DroughtWatch AI V2, a multivariate, pixel-level system that forecasts six months of future Normalized Difference Vegetation Index (NDVI) independently across 25,522 individual cropland pixels (250 m resolution, ESA WorldCover-masked) in the Faiyum Governorate, Egypt, using a 300-month (2001–2025) multivariate satellite and reanalysis record — NDVI, EVI, land-surface temperature, precipitation, and root-zone soil moisture from MODIS, CHIRPS, and GLDAS. A stacked bidirectional Long Short-Term Memory (Bi-LSTM) network, trained on 12-month input sequences pooled across 1,500 randomly sampled pixels, was evaluated on a chronologically buffered (24-month gap), out-of-sample test set of 20,400 sequences against a per-pixel, calendar-month-matched Historical Seasonal Climatology baseline. The model reduced MAE and RMSE by approximately 28% relative to this baseline (MAE 0.0413 vs. 0.0572; RMSE 0.0536 vs. 0.0741) and explained 87.6% of out-of-sample variance versus 76.3% for the baseline (R²). The underlying project line was awarded Third Prize at the 2026 Global Environment Solutions Challenge (GESC), organized under the UNEP–Tongji Institute of Environment for Sustainable Development, among 574 teams from 58 countries.',
    abstractAr: 'يُعد الجفاف كارثة بطيئة الحدوث تتضاعف أضرارها الاقتصادية نتيجة قصر المدى الزمني للإنذار في النظم الحالية. توثق هذه الورقة البحثية نظام DroughtWatch AI V2، وهو نظام تنبؤي متعدد المتغيرات يعمل على مستوى البكسل الفردي للتنبؤ بمؤشر الغطاء النباتي (NDVI) لستة أشهر قادمة بشكل مستقل عبر 25,522 بكسل زراعي (بدقة 250 متراً وبقناع غطاء الأراضي ESA WorldCover) في محافظة الفيوم بمصر، بالاعتماد على سجل فضائي وبيانات إعادة التحليل المناخي تمتد لـ 300 شهر (2001-2025) تضم: NDVI و EVI وحرارة السطح (LST) والأمطار (CHIRPS) ورطوبة التربة الجذرية (GLDAS). تم تدريب شبكة Bi-LSTM متراكمة وتجربتها على 20,400 تسلسل اختباري مفصول بفاصل زمني صارم 24 شهراً لمنع تسرب البيانات. حقق النموذج تخفيضاً في الخطأ بنسبة 28% تقريباً (MAE 0.0413 و RMSE 0.0536) وفسّر 87.6% من التباين المستقبلي مقارنة بالمتوسط المناخي. حاز المشروع على الجائزة الثالثة عالمياً في تحدي الحلول البيئية العالمي (GESC 2026) تحت مظلة معهد البيئة للتنمية المستدامة المشترك بين برنامج الأمم المتحدة للبيئة (UNEP) وجامعة تونغجي، من بين 574 فريقاً من 58 دولة.',
    doi: '10.13140/RG.2.2.25563.53287',
    researchGateUrl: 'https://www.researchgate.net/publication/412631279_DroughtWatch_AI_V2_Multivariate_Bidirectional_LSTM_Framework_for_Pixel-Level_Agricultural_Drought_Early_Warning_in_Faiyum_Egypt',
    pdfUrl: '/research/papers/DroughtWatch_AI_V2_Paper.pdf',
    license: 'CC BY 4.0',
    awardEn: 'Global 3rd Prize Winner — 2026 Global Environment Solutions Challenge (UNEP–Tongji Institute, 574 Teams / 58 Countries)',
    awardAr: 'الفائز بالجائزة الثالثة عالمياً — تحدي الحلول البيئية العالمي GESC 2026 (معهد UNEP-Tongji، بين 574 فريقاً من 58 دولة)',
    keyFindingsEn: [
      'Pixel-level multivariate forecasting across 25,522 individual cropland pixels (250 m resolution, ESA WorldCover).',
      'Unified fusion of 5 satellite & reanalysis streams: MODIS NDVI, EVI, Land Surface Temp, CHIRPS precipitation, and NASA GLDAS root-zone soil moisture.',
      'Out-of-sample evaluation on 20,400 sequences with a 24-month buffer gap achieving MAE 0.0413, RMSE 0.0536, and R² 0.8762 (28% error reduction over per-pixel baseline).',
      'FastAPI deployment with ~180 ms deterministic latency and strict feature-ranges training domain boundary enforcement.'
    ],
    keyFindingsAr: [
      'تنبؤ مكاني متعدد المتغيرات على مستوى 25,522 بكسل زراعي بدقة 250 متراً باستخدام قناع ESA WorldCover.',
      'دمج 5 روافد بيئية وهيدرولوجية فضائية: MODIS NDVI، EVI، حرارة السطح، أمطار CHIRPS، ورطوبة التربة من NASA GLDAS.',
      'تقييم على 20,400 تسلسل اختباري بفاصل زمني 24 شهراً حقق MAE قدره 0.0413 و RMSE قدره 0.0536 و R² يبلغ 0.8762 (تخفيض 28% في الخطأ).',
      'نشر تشغيلي متكامل عبر واجهة FastAPI بزمن استجابة 180 مللي ثانية مع نظام حماية نطاق التدريب (Domain Boundaries).'
    ],
    isSpotlight: true,
  },
  {
    id: 'droughtwatch-ai-v1-bilstm-2026',
    titleEn: 'DroughtWatch AI: A Bidirectional LSTM Framework for Six-Month-Ahead NDVI Forecasting and Agricultural Drought Early Warning: A Proof-of-Concept Case Study from the Faiyum Region, Egypt',
    titleAr: 'نظام DroughtWatch AI: إطار عمل لشبكات LSTM ثنائية الاتجاه للتنبؤ بمؤشر الغطاء النباتي (NDVI) قبل 6 أشهر والإنذار المبكر بالجفاف الزراعي في محافظة الفيوم، مصر',
    category: 'climate',
    categoryLabelEn: 'Climate & Drought AI',
    categoryLabelAr: 'ذكاء المناخ والجفاف',
    date: 'July 2026',
    authorsEn: 'Abdelrahman M. D. S. A. Abushousha',
    authorsAr: 'عبدالرحمن محمد دياب شوشة (Abdelrahman Abushousha)',
    abstractEn: 'This paper documents the methodology, results, and structural validation of DroughtWatch AI, a framework that forecasts six months of future Normalized Difference Vegetation Index (NDVI) values from a 25-year (2000–2025) satellite-derived NDVI record for an agricultural zone in the Faiyum Governorate, Egypt. To ensure scientific rigor, we implement an airtight 24-step temporal buffer gap strictly between the training and testing datasets before sequence construction, completely eliminating data leakage from sliding window overlap. A stacked bidirectional Long Short-Term Memory (Bi-LSTM) network was trained on 12-month input sequences to output 6-month-ahead NDVI forecasts. The proposed Bi-LSTM model achieves absolute dominance over a Historical Seasonal Climatology baseline (which underperforms with an MAE of 0.0699, RMSE of 0.0834, and R² of -0.2580), reaching a definitive MAE of 0.0214 NDVI units, an RMSE of 0.0264, and a coefficient of determination (R²) of 0.8740 on the out-of-sample test set. In an illustrative retrospective case study, a forecast issued in September 2024 anticipated a decline in NDVI that closely matched independently obtained satellite observations from February 2025.',
    abstractAr: 'توثق هذه الورقة البحثية المنهجية والنتائج والتحقق الهيكلي لنظام DroughtWatch AI، وهو إطار عمل يتنبأ بقيم مؤشر الغطاء النباتي (NDVI) لستة أشهر قادمة بناءً على سجل فضائي يمتد لـ 25 عاماً (2000-2025) لمنطقة زراعية في محافظة الفيوم بمصر. لضمان الصرامة العلمية، تم تطبيق فاصل زمني صارم قدره 24 خطوة زمنية بين مجموعتي التدريب والاختبار قبل إنشاء التسلسلات لمنع أي تسرب للبيانات. تم تدريب شبكة Bi-LSTM متراكمة على نوافذ زمنية مدتها 12 شهراً لإصدار تنبؤات لـ 6 أشهر قادمة. حقق النموذج تفوقاً كاملاً على خط الأساس المناخي الموسمي محققاً خطأ MAE قدره 0.0214 و RMSE قدره 0.0264 و R² يبلغ 0.8740. وفي دراسة استرجاعية توضيحية، تنبأ النموذج في سبتمبر 2024 بانخفاض حاد في الغطاء النباتي تطابق بدقة مذهلة مع رصد الأقمار الصناعية الفعلي في فبراير 2025.',
    doi: '10.13140/RG.2.2.22395.89125',
    researchGateUrl: 'https://www.researchgate.net/publication/408839478_DroughtWatch_AI_Bidirectional_LSTM_Framework_for_Six-Month_Agricultural_Drought_Early_Warning_in_Faiyum_Egypt',
    pdfUrl: '/research/papers/DroughtWatch_AI_V1_Paper.pdf',
    license: 'CC BY 4.0',
    awardEn: 'Global 3rd Prize Winner — 2026 Global Environment Solutions Challenge (UNEP–Tongji Institute, 574 Teams / 58 Countries)',
    awardAr: 'الفائز بالجائزة الثالثة عالمياً — تحدي الحلول البيئية العالمي GESC 2026 (معهد UNEP-Tongji، بين 574 فريقاً من 58 دولة)',
    keyFindingsEn: [
      '6-month ahead NDVI forecasting with test-set MAE of 0.0214, RMSE of 0.0264, and R² of 0.8740 over 25-year satellite record (2000–2025).',
      '69.4% MAE reduction over Historical Seasonal Climatology baseline with an airtight 24-step chronological buffer gap eliminating data leakage.',
      'Monte Carlo Dropout uncertainty estimation achieving 87.2% probability early warning verified against Feb 2025 MODIS satellite observations.',
      'End-to-end FastAPI deployment with 184 ms inference latency for real-time agricultural decision support.'
    ],
    keyFindingsAr: [
      'التنبؤ بمؤشر NDVI قبل 6 أشهر بخطأ MAE قدره 0.0214 و RMSE قدره 0.0264 ومعامل تحديد R² يبلغ 0.8740 على مدار 25 عاماً من بيانات الأقمار الصناعية (2000-2025).',
      'تخفيض خطأ التنبؤ بنسبة 69.4% مقارنة بالمتوسط المناخي الموسمي مع عزل زمني صارم (24 خطوة) لمنع تسرب البيانات (Data Leakage).',
      'تقدير احتمالي لعدم اليقين عبر Monte Carlo Dropout بدقة إنذار مبكر بلغت 87.2% تم التحقق منها ميدانياً بصور أقمار MODIS لفبراير 2025.',
      'نشر تشغيلي متكامل عبر FastAPI بزمن استجابة 184 مللي ثانية لدعم اتخاذ القرار الزراعي في الوقت الفعلي.'
    ]
  }
];

export const ResearchOverview: React.FC<ResearchOverviewProps> = ({ lang }) => {
  const isAr = lang === 'ar';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [activePaperModal, setActivePaperModal] = useState<ResearchPaper | null>(null);
  const [copiedBibtexId, setCopiedBibtexId] = useState<string | null>(null);

  // Filter papers based on search and category
  const filteredPapers = useMemo(() => {
    return RESEARCH_PAPERS.filter((paper) => {
      const matchesCategory = selectedCategory === 'all' || paper.category === selectedCategory;
      const title = isAr ? paper.titleAr : paper.titleEn;
      const abstract = isAr ? paper.abstractAr : paper.abstractEn;
      const authors = isAr ? paper.authorsAr : paper.authorsEn;
      const q = searchQuery.toLowerCase().trim();

      const matchesSearch =
        !q ||
        title.toLowerCase().includes(q) ||
        abstract.toLowerCase().includes(q) ||
        authors.toLowerCase().includes(q) ||
        paper.doi.toLowerCase().includes(q);

      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory, isAr]);

  const spotlightPaper = useMemo(() => {
    return RESEARCH_PAPERS.find((p) => p.isSpotlight) || RESEARCH_PAPERS[0] || null;
  }, []);

  const secondarySpotlights = useMemo(() => {
    if (!spotlightPaper) return [];
    return RESEARCH_PAPERS.filter((p) => p.id !== spotlightPaper.id).slice(0, 4);
  }, [spotlightPaper]);

  const categories = [
    { id: 'all', labelEn: 'All Research', labelAr: 'جميع الأبحاث' },
    { id: 'climate', labelEn: 'Climate & Drought AI', labelAr: 'ذكاء المناخ والجفاف' },
    { id: 'foundation', labelEn: 'Spatial Foundation Models', labelAr: 'نماذج الأساس المكانية' },
    { id: 'rag', labelEn: 'Spatial RAG', labelAr: 'الاستدلال المكاني' },
    { id: 'swarms', labelEn: 'Autonomous Agents', labelAr: 'العملاء المستقلون' },
    { id: 'safety', labelEn: 'Topological Safety', labelAr: 'الأمان الطوبولوجي' },
  ];

  const handleCopyBibtex = (paper: ResearchPaper) => {
    const bibtex = `@article{abushousha_${paper.id.replace(/-/g, '_')},
  title={${paper.titleEn}},
  author={${paper.authorsEn}},
  journal={ResearchGate / VEXL Research Labs},
  year={2026},
  doi={${paper.doi}},
  url={${paper.researchGateUrl}}
}`;
    navigator.clipboard.writeText(bibtex);
    setCopiedBibtexId(paper.id);
    setTimeout(() => setCopiedBibtexId(null), 2500);
  };

  return (
    <div
      className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 ${isAr ? 'font-arabic' : 'font-sans'} antialiased`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <SEO
        title={isAr ? 'أبحاث الذكاء المكاني والرؤية العلمية | مختبرات فكسل' : 'Spatial Intelligence Research | VEXL Research Labs'}
        description={isAr ? 'المختبر العلمي الرائد لأبحاث نماذج الأساس المكانية، الاستدلال الطوبولوجي، وعملاء الذكاء الاصطناعي الجغرافي المستقلة.' : 'Investigating the frontier of spatial intelligence, multi-modal Earth observation, and autonomous GeoAI reasoning agents.'}
        lang={lang}
      />

      {/* ── Editorial Top Anchor Bar ────────────────────────────────────────── */}
      <section className="pt-28 md:pt-32 pb-16 border-b border-[#262C31] relative">
        <div className="w-full max-w-[1450px] mx-auto px-6 relative z-10">

          {/* Main Title & Mission Block (Anthropic-style Hero) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#EDF0F1] leading-[1.05]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {isAr ? 'الأبحاث والابتكار' : 'Research'}
              </h1>
            </div>

            <div className="lg:col-span-6 space-y-6">
              <p className="text-base sm:text-lg text-[#9AA5AC] font-normal leading-relaxed">
                {isAr
                  ? 'تستكشف فرقنا البحثية الآفاق المتقدمة لنماذج الأساس المكانية، والاستدلال الطوبولوجي المعزز، وأنظمة عملاء الذكاء الاصطناعي الجغرافي المستقلة — لتمكين الذكاء المكاني من إحداث تأثير علمي موثوق في مواجهة تغيرات المناخ، وإدارة الموارد المائية، وأتمتة هندسة البيانات الأرضية.'
                  : 'Our research teams investigate the frontier of spatial foundation models, topological reasoning, and autonomous GeoAI agents — so that spatial intelligence has a mathematically sound, reliable impact on global climate resilience, environmental governance, and planetary data engineering.'}
              </p>

              {/* Research Teams Quick Links */}
              <div className="pt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium text-[#9AA5AC]">
                <span className="text-[#626F79] font-mono uppercase tracking-widest text-[11px]">
                  {isAr ? 'محاور التركيز:' : 'Research Teams:'}
                </span>
                <a href="#pillars" className="hover:text-[#0E93A3] transition-colors border-b border-dotted border-[#262C31] hover:border-[#0E93A3] pb-0.5">
                  {isAr ? 'نماذج الأساس الجغرافية' : 'Spatial Foundation Models'}
                </a>
                <a href="#pillars" className="hover:text-[#0E93A3] transition-colors border-b border-dotted border-[#262C31] hover:border-[#0E93A3] pb-0.5">
                  {isAr ? 'الاستدلال المكاني و RAG' : 'Spatial RAG & Reasoning'}
                </a>
                <a href="#pillars" className="hover:text-[#0E93A3] transition-colors border-b border-dotted border-[#262C31] hover:border-[#0E93A3] pb-0.5">
                  {isAr ? 'ذكاء المناخ والجفاف' : 'Climate & DroughtWatch'}
                </a>
                <a href="#pillars" className="hover:text-[#0E93A3] transition-colors border-b border-dotted border-[#262C31] hover:border-[#0E93A3] pb-0.5">
                  {isAr ? 'أنظمة عملاء GIS المستقلة' : 'Autonomous GIS Agent Systems'}
                </a>
                <a href="#publications" className="hover:text-[#0E93A3] transition-colors border-b border-dotted border-[#262C31] hover:border-[#0E93A3] pb-0.5">
                  {isAr ? 'الأمان والتحقق الطوبولوجي' : 'Topological Safety'}
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Multi-Scale Spatial Modalities Showcase Banner ─────────────────── */}
      <section className="py-12 border-b border-black/[0.08] dark:border-white/[0.08] bg-black/[0.01] dark:bg-[#030508]">
        <div className="w-full max-w-6xl mx-auto px-6">
          <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#07090E] overflow-hidden shadow-2xl relative select-none">

            {/* Image Container with Anti-Copy & Anti-Drag Security Shield */}
            <div
              className="relative w-full aspect-[1663/945] overflow-hidden select-none bg-[#030508]"
              style={{ aspectRatio: '1663 / 945' }}
              onContextMenu={(e) => e.preventDefault()}
            >
              {/* Image with strict anti-drag, anti-select, static (no hover movement) */}
              <img
                src="/images/research/spatial-modalities-mosaic.jpg"
                alt={isAr ? 'الأنماط والمصفوفات المكانية المتعددة لمختبرات فكسل' : 'Multi-Scale Spatial Modalities Mosaic'}
                width={1663}
                height={945}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                className="w-full h-full object-cover select-none pointer-events-none"
              />

              {/* Protective Interaction Barrier Overlay (Prevents right-click inspect, saving, and image dragging) */}
              <div
                className="absolute inset-0 z-10 select-none bg-gradient-to-t from-black/85 via-black/15 to-transparent pointer-events-auto"
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              />

              {/* Bottom Overlay Badges & Captions */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 z-20 flex flex-col sm:flex-row sm:items-end justify-between gap-4 pointer-events-none">
                <div className="space-y-1 max-w-xl text-white">
                  <div className="text-[11px] font-mono text-[#0E93A3] font-bold tracking-widest uppercase">
                    {isAr ? 'استكشاف الأنماط الأرضية' : 'EARTH OBSERVATION FUSION'}
                  </div>
                  <h3 className="text-base sm:text-xl font-bold leading-snug drop-shadow-md">
                    {isAr
                      ? 'من مدارات الأقمار الصناعية إلى مصفوفات المحاصيل الزراعية بدقة 250 متراً'
                      : 'From Orbital Satellite Swaths to 250m Cropland Precision Grids'}
                  </h3>
                  <p className="text-xs text-slate-300 font-light leading-relaxed hidden md:block">
                    {isAr
                      ? 'دمج متكامل لبيانات الارتفاعات الرقمية، الشبكات الهيدرولوجية، خرائط استخدامات الأراضي، ومسارات النقل في نموذج معرفي مكاني موحد.'
                      : 'Unifying digital elevation contours, hydrographic networks, land cover classifications, and topological graphs into unified spatial AI representations.'}
                  </p>
                </div>

                {/* Modality Chips */}
                <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                  <span className="px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md border border-white/20 text-white text-[10px] font-mono">
                    {isAr ? 'شبكات المحاصيل (NDVI)' : 'Cropland Grids'}
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md border border-white/20 text-white text-[10px] font-mono">
                    {isAr ? 'المناسيب الكنتورية (DEM)' : 'Elevation Contours'}
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md border border-white/20 text-white text-[10px] font-mono">
                    {isAr ? 'الشبكات الهيدرولوجية' : 'Hydro Networks'}
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md border border-white/20 text-white text-[10px] font-mono">
                    {isAr ? 'المسارات المدارية' : 'Orbital Swaths'}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Section 2: 5-Column Core Pillars (Horizontal Editorial Grid) ───── */}
      <section id="pillars" className="py-16 border-b border-[#262C31]">
        <div className="w-full max-w-[1450px] mx-auto px-6">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6 divide-y lg:divide-y-0 lg:divide-x divide-[#262C31] rtl:divide-x-reverse">

            {/* Pillar 1 */}
            <div className="space-y-3 pt-6 lg:pt-0 lg:px-4 first:lg:pl-0 last:lg:pr-0">
              <h3 className="text-base font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'نماذج الأساس الجغرافية' : 'Spatial Foundation Models'}
              </h3>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'بناء محولات متعددة الأنماط تستوعب خرائط المتجهات، والأطياف الفضائية 13-band، ورادارات SAR في فضاء إحداثي مستمر.'
                  : 'Pre-training multi-modal transformers over petabytes of multi-spectral satellite imagery, SAR radar, and topological vector graphs.'}
              </p>
            </div>

            {/* Pillar 2 */}
            <div className="space-y-3 pt-6 lg:pt-0 lg:px-4">
              <h3 className="text-base font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'الاستدلال المكاني و RAG' : 'Spatial RAG & Reasoning'}
              </h3>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'تطوير آليات استرجاع موجهة بالرسوم البيانية تفرض قيود الطوبولوجيا وDE-9IM لمنع الهلوسة الجغرافية في النماذج اللغوية.'
                  : 'Graph-guided spatial retrieval systems that strictly enforce DE-9IM intersection invariants and eliminate distance hallucinations.'}
              </p>
            </div>

            {/* Pillar 3 */}
            <div className="space-y-3 pt-6 lg:pt-0 lg:px-4">
              <h3 className="text-base font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'ذكاء المناخ و DroughtWatch' : 'Climate & Drought AI'}
              </h3>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'شبكات عصبية موجهة فيزيائياً لنمذجة ديناميكا الرطوبة والتنبؤ بالجفاف الإقليمي 6 أشهر مسبقاً بدقة فيزيائية 94.2%.'
                  : 'Physics-informed neural networks modeling evapotranspiration and soil moisture anomalies 180 days in advance.'}
              </p>
            </div>

            {/* Pillar 4 */}
            <div className="space-y-3 pt-6 lg:pt-0 lg:px-4">
              <h3 className="text-base font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'أنظمة عملاء GIS المستقلة' : 'Autonomous GIS Agent Systems'}
              </h3>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'صياغة أنظمة ذكاء اصطناعي متعددة الوكلاء قادرة على كتابة كود ArcPy و PostGIS وتصحيح الأخطاء الهندسية تلقائياً.'
                  : 'Multi-agent reasoning loops capable of authoring, executing, and self-healing complex spatial workflows and GeoSQL.'}
              </p>
            </div>

            {/* Pillar 5 */}
            <div className="space-y-3 pt-6 lg:pt-0 lg:px-4">
              <h3 className="text-base font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'الأمان الطوبولوجي والمعايرة' : 'Topological Safety'}
              </h3>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'بروتوكولات معايرة أرضية حقلية (Ground-Truth) تضمن دقة الحدود وتوافق النماذج مع المعايير الجيوديسية الدولية.'
                  : 'Ground-truth sensor calibration and boundary verification ensuring zero geometric violations in high-stakes decisions.'}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* ── Section 3: Featured Breakthroughs Spotlight (Magazine Layout) ─── */}
      {spotlightPaper && (
        <section className="py-20 border-b border-[#262C31] bg-[#05070A]">
          <div className="w-full max-w-[1450px] mx-auto px-6">

            <div className="flex items-center justify-between mb-12">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] mb-2 font-semibold flex items-center gap-2">
                  <Award size={14} className="text-[#D48207]" />
                  <span>{isAr ? 'البحث العلمي المميز والجوائز العالمية' : 'FEATURED RESEARCH & GLOBAL AWARDS'}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#EDF0F1] tracking-tight">
                  {isAr ? 'أوراق العمل والمحاور الرئيسية' : 'Breakthrough Papers & Technical Insights'}
                </h2>
              </div>
              <a
                href="#publications"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#9AA5AC] hover:text-[#0E93A3] transition-colors"
              >
                <span>{isAr ? 'عرض كل الأوراق البحثية' : 'View all publications'}</span>
                <ChevronRight size={14} className={isAr ? 'rotate-180' : ''} />
              </a>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

              {/* Main Featured Spotlight Card (Left 7 Cols) */}
              <div
                className="lg:col-span-7 p-8 rounded-md border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/40 transition-all duration-300 flex flex-col justify-between group shadow-sm relative"
              >
                {/* Scientific Architecture Diagram for DroughtWatch V2 (Clean SVG Box) */}
                <div className="w-full h-60 rounded bg-[#05070A] border border-[#262C31] mb-8 p-5 flex flex-col justify-between relative overflow-hidden">
                  {/* SVG Architecture Wireframe */}
                  <svg className="w-full h-full text-slate-700" viewBox="0 0 620 190" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Grid Lines */}
                    <line x1="40" y1="150" x2="580" y2="150" stroke="currentColor" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.25" />
                    <line x1="40" y1="100" x2="580" y2="100" stroke="currentColor" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.25" />
                    <line x1="40" y1="50" x2="580" y2="50" stroke="currentColor" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.25" />

                    {/* 5 Multi-variate Band Waveforms */}
                    {/* NDVI */}
                    <path d="M 50 140 Q 120 30, 200 85 T 350 95 T 480 40 T 570 135" stroke="#10B981" strokeWidth="2.2" fill="none" opacity="0.95" />
                    {/* EVI */}
                    <path d="M 50 120 Q 140 160, 230 65 T 370 115 T 570 75" stroke="#0E93A3" strokeWidth="1.5" fill="none" strokeDasharray="4 2" opacity="0.8" />
                    {/* LST (Thermal) */}
                    <path d="M 50 65 Q 160 25, 270 125 T 450 65 T 570 110" stroke="#D48207" strokeWidth="1.5" fill="none" opacity="0.75" />
                    {/* Soil Moisture GLDAS */}
                    <path d="M 50 155 Q 180 145, 300 45 T 470 130 T 570 90" stroke="#3B82F6" strokeWidth="1.5" fill="none" opacity="0.7" />

                    {/* Network Nodes */}
                    <circle cx="200" cy="85" r="4.5" fill="#10B981" />
                    <circle cx="350" cy="95" r="4.5" fill="#0E93A3" />
                    <circle cx="480" cy="40" r="4.5" fill="#D48207" />

                    {/* Connectors */}
                    <line x1="200" y1="85" x2="350" y2="95" stroke="#0E93A3" strokeWidth="1" strokeDasharray="2 2" opacity="0.7" />
                    <line x1="350" y1="95" x2="480" y2="40" stroke="#D48207" strokeWidth="1" strokeDasharray="2 2" opacity="0.7" />
                  </svg>

                  <div className="flex flex-wrap items-center justify-between text-[10px] font-mono text-[#9AA5AC] z-10 gap-2">
                    <span className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                      <span>MULTIVARIATE_BI-LSTM // 25,522 CROPLAND PIXELS (250m)</span>
                    </span>
                    <span className="text-[#0E93A3] font-bold">5 BANDS · 24-MO BUFFER · 6-MO HORIZON</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Global Award Pill */}
                  {spotlightPaper.awardEn && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-amber-500/10 border border-amber-500/30 text-[#D48207] text-xs font-semibold">
                      <Award size={14} className="shrink-0" />
                      <span>{isAr ? spotlightPaper.awardAr : spotlightPaper.awardEn}</span>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <span className="px-2.5 py-1 rounded-sm bg-[#0E93A3]/10 border border-[#0E93A3]/20 text-[#0E93A3] text-xs font-mono font-semibold">
                      {isAr ? spotlightPaper.categoryLabelAr : spotlightPaper.categoryLabelEn}
                    </span>
                    <span className="text-xs font-mono text-[#9AA5AC]">{spotlightPaper.date}</span>
                    <span className="text-xs font-mono text-[#9AA5AC]">DOI: {spotlightPaper.doi}</span>
                  </div>

                  <h3
                    onClick={() => setActivePaperModal(spotlightPaper)}
                    className="text-xl sm:text-2xl font-bold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-tight cursor-pointer"
                  >
                    {isAr ? spotlightPaper.titleAr : spotlightPaper.titleEn}
                  </h3>

                  <p className="text-sm text-[#9AA5AC] leading-relaxed line-clamp-3">
                    {isAr ? spotlightPaper.abstractAr : spotlightPaper.abstractEn}
                  </p>
                </div>

                {/* Footer Controls & Direct Links */}
                <div className="pt-6 mt-6 border-t border-[#262C31] flex flex-wrap items-center justify-between gap-4 text-xs">
                  <span className="text-[#9AA5AC] font-mono">{isAr ? spotlightPaper.authorsAr : spotlightPaper.authorsEn}</span>

                  <div className="flex items-center gap-3">
                    <a
                      href={spotlightPaper.pdfUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#262C31] bg-[#171C21] text-[#EDF0F1] hover:text-[#0E93A3] hover:border-[#0E93A3]/40 transition-all font-mono font-semibold"
                    >
                      <Download size={12} />
                      <span>{isAr ? 'تحميل PDF' : 'Download PDF'}</span>
                    </a>
                    <button
                      onClick={() => setActivePaperModal(spotlightPaper)}
                      className="inline-flex items-center gap-1 text-[#0E93A3] font-semibold hover:underline cursor-pointer"
                    >
                      <span>{isAr ? 'عرض المستخلص والنتائج' : 'Read Abstract & Findings'}</span>
                      <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Secondary Spotlights List (Right 5 Cols) */}
              <div className="lg:col-span-5 flex flex-col justify-between gap-4">
                {secondarySpotlights.map((paper) => (
                  <div
                    key={paper.id}
                    className="p-6 rounded-md border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/40 transition-all duration-200 group flex flex-col justify-between shadow-sm"
                  >
                    <div>
                      {paper.awardEn && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-amber-500/10 border border-amber-500/20 text-[#D48207] text-[11px] font-semibold mb-3">
                          <Award size={12} />
                          <span>{isAr ? paper.awardAr : paper.awardEn}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-[11px] font-mono text-[#9AA5AC] mb-2">
                        <span className="text-[#9AA5AC] font-semibold">
                          {isAr ? paper.categoryLabelAr : paper.categoryLabelEn}
                        </span>
                        <span>{paper.date}</span>
                      </div>

                      <h4
                        onClick={() => setActivePaperModal(paper)}
                        className="text-sm font-bold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug cursor-pointer"
                      >
                        {isAr ? paper.titleAr : paper.titleEn}
                      </h4>

                      <p className="text-xs text-[#9AA5AC] mt-2 line-clamp-3 leading-relaxed">
                        {isAr ? paper.abstractAr : paper.abstractEn}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-[#262C31] flex items-center justify-between text-[11px] font-mono">
                      <a
                        href={paper.pdfUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#9AA5AC] hover:text-[#0E93A3] transition-colors"
                      >
                        <Download size={11} />
                        <span>PDF</span>
                      </a>
                      <button
                        onClick={() => setActivePaperModal(paper)}
                        className="text-[#0E93A3] group-hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                      >
                        <span>{isAr ? 'التفاصيل' : 'Details'}</span>
                        <ExternalLink size={10} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

            </div>

          </div>
        </section>
      )}

      {/* ── Section 4: Interactive Publications Archive (Table + Filters) ─── */}
      <section id="publications" className="py-20 border-b border-black/[0.08] dark:border-white/[0.08]">
        <div className="w-full max-w-[1450px] mx-auto px-6">

          {/* Header & Search / Filters Controls */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] mb-2 font-semibold">
                {isAr ? 'الأرشيف الأكاديمي الرسمي' : 'OFFICIAL SCIENTIFIC ARCHIVE'}
              </div>
              <h2 className="text-3xl font-bold text-[#EDF0F1] tracking-tight">
                {isAr ? 'الأوراق البحثية والمطبوعات' : 'Publications & Preprints'}
              </h2>
            </div>

            {/* Live Search Input */}
            <div className="relative w-full md:w-80">
              <Search size={15} className={`absolute top-1/2 -translate-y-1/2 text-[#9AA5AC] ${isAr ? 'right-3.5' : 'left-3.5'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isAr ? 'ابحث عن ورقة، عنوان، أو مؤلف...' : 'Search papers, topics, or authors...'}
                className={`w-full py-2.5 rounded-sm border border-[#262C31] bg-[#12161A] text-xs text-[#EDF0F1] placeholder-[#626F79] focus:outline-none focus:border-[#0E93A3] focus:ring-1 focus:ring-[#0E93A3] transition-all ${isAr ? 'pr-9 pl-4' : 'pl-9 pr-4'
                  }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute top-1/2 -translate-y-1/2 text-[#9AA5AC] hover:text-[#EDF0F1] ${isAr ? 'left-3' : 'right-3'}`}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center gap-2 mb-8 pb-4 border-b border-[#262C31]">
            <span className="text-xs font-mono text-[#9AA5AC] mr-2 flex items-center gap-1">
              <Filter size={12} />
              <span>{isAr ? 'التصنيف:' : 'Domain:'}</span>
            </span>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-1.5 rounded-sm text-xs font-medium transition-all cursor-pointer ${selectedCategory === cat.id
                  ? 'bg-[#0E93A3] text-white font-semibold shadow-sm'
                  : 'bg-[#12161A] text-[#9AA5AC] hover:text-[#EDF0F1] hover:bg-[#171C21] border border-[#262C31]'
                  }`}
              >
                {isAr ? cat.labelAr : cat.labelEn}
              </button>
            ))}
          </div>

          {/* Publications Table (Anthropic Editorial Style) */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" dir={isAr ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="border-b border-[#262C31] text-[11px] font-mono text-[#9AA5AC] uppercase tracking-wider">
                  <th className="py-4 px-4 font-semibold w-32">{isAr ? 'التاريخ' : 'DATE'}</th>
                  <th className="py-4 px-4 font-semibold w-48">{isAr ? 'المجال' : 'DOMAIN'}</th>
                  <th className="py-4 px-4 font-semibold">{isAr ? 'عنوان الورقة البحثية والمستخلص' : 'TITLE & ABSTRACT'}</th>
                  <th className="py-4 px-4 font-semibold text-right w-44">{isAr ? 'الإجراءات والتحميل' : 'ACTIONS'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#262C31]">
                {filteredPapers.map((paper) => (
                  <tr
                    key={paper.id}
                    onClick={() => setActivePaperModal(paper)}
                    className="hover:bg-[#12161A]/60 transition-colors cursor-pointer group"
                  >
                    {/* Date */}
                    <td className="py-5 px-4 text-xs font-mono text-[#9AA5AC] align-top whitespace-nowrap">
                      <div className="text-[#EDF0F1] font-semibold">{paper.date}</div>
                      <div className="text-[10px] text-[#9AA5AC] mt-1">{paper.license}</div>
                    </td>

                    {/* Category Tag */}
                    <td className="py-5 px-4 align-top">
                      <span className="inline-block px-2.5 py-0.5 rounded-sm bg-[#171C21] border border-[#262C31] text-[#9AA5AC] text-[11px] font-mono">
                        {isAr ? paper.categoryLabelAr : paper.categoryLabelEn}
                      </span>
                    </td>

                    {/* Title & Abstract Preview */}
                    <td className="py-5 px-4 align-top space-y-2 max-w-2xl">
                      {paper.awardEn && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/20 text-[#D48207] text-[11px] font-semibold">
                          <Award size={12} />
                          <span>{isAr ? paper.awardAr : paper.awardEn}</span>
                        </div>
                      )}
                      <h4 className="text-sm font-bold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug">
                        {isAr ? paper.titleAr : paper.titleEn}
                      </h4>
                      <p className="text-xs text-[#9AA5AC] leading-relaxed line-clamp-2">
                        {isAr ? paper.abstractAr : paper.abstractEn}
                      </p>
                      <div className="text-[11px] font-mono text-[#9AA5AC]">
                        {isAr ? paper.authorsAr : paper.authorsEn} · <span className="text-[#9AA5AC]">DOI: {paper.doi}</span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="py-5 px-4 text-right align-top whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={paper.pdfUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          title={isAr ? 'تحميل ملف البحث (PDF)' : 'Download PDF'}
                          className="p-2 rounded-sm border border-[#262C31] bg-[#12161A] text-[#9AA5AC] hover:text-[#0E93A3] hover:border-[#0E93A3]/40 transition-colors inline-flex items-center justify-center cursor-pointer"
                        >
                          <Download size={13} />
                        </a>
                        <a
                          href={paper.researchGateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={isAr ? 'عرض على ResearchGate' : 'View on ResearchGate'}
                          className="p-2 rounded-sm border border-[#262C31] bg-[#12161A] text-[#9AA5AC] hover:text-[#EDF0F1] hover:border-[#0E93A3]/40 transition-colors inline-flex items-center justify-center cursor-pointer"
                        >
                          <ExternalLink size={13} />
                        </a>
                        <button
                          onClick={() => handleCopyBibtex(paper)}
                          title={isAr ? 'نسخ استشهاد BibTeX' : 'Copy BibTeX Citation'}
                          className="p-2 rounded-sm border border-[#262C31] bg-[#12161A] text-[#9AA5AC] hover:text-[#EDF0F1] hover:border-[#0E93A3]/40 transition-colors cursor-pointer"
                        >
                          {copiedBibtexId === paper.id ? <Check size={13} className="text-[#0E93A3]" /> : <Copy size={13} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPapers.length === 0 && (
              <div className="py-16 text-center text-[#9AA5AC] space-y-2">
                <FileText size={32} className="mx-auto text-[#626F79]" />
                <p className="text-sm">
                  {RESEARCH_PAPERS.length === 0
                    ? (isAr ? 'جاري تجهيز وفهرسة الأبحاث والمنشورات العلمية الرسمية.' : 'Official publications and technical reports are currently being indexed.')
                    : (isAr ? 'لم يتم العثور على أبحاث مطابقة لمعايير البحث.' : 'No research papers matched your search criteria.')}
                </p>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* ── Section 5: Scientific Alliances & Satellite Constellations ─────── */}
      <section className="py-20 border-b border-black/[0.08] dark:border-white/[0.08]">
        <div className="w-full max-w-[1450px] mx-auto px-6">

          <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
            <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
              {isAr ? 'التحقق العلمي والبيانات العالمية' : 'SCIENTIFIC VALIDATION & CONSTELLATIONS'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EDF0F1] tracking-tight">
              {isAr ? 'شراكات البيانات والمطابقة الأرضية' : 'Calibrated Against Planetary Satellite Networks'}
            </h2>
            <p className="text-sm text-[#9AA5AC] leading-relaxed">
              {isAr
                ? 'تتم معايرة نماذج VEXL الرياضية واختبارها مقابل كوكبات الأقمار الصناعية الدولية ومحطات القياس الحقلية لضمان الدقة الفيزيائية للنتائج.'
                : 'Our spatial foundation models and climate prediction indices are calibrated directly against empirical ground-truth sensors and international Earth observation fleets.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                [CONSTELLATION // OPTICAL]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">Copernicus Sentinel-2</h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr ? '13 نطاقاً طيفياً بدقة 10 أمتار لرصد الغطاء النباتي والمائي كل 5 أيام.' : '13 spectral bands at 10m resolution for multispectral vegetation & water analysis.'}
              </p>
            </div>

            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                [CONSTELLATION // THERMAL]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">Landsat-9 TIRS & OLI</h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr ? 'مستشعرات الأشعة تحت الحمراء الحرارية لقياس درجات حرارة أسطح التربة.' : 'Thermal infrared sensor suite for planetary surface temperature modeling.'}
              </p>
            </div>

            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#4E9BB8] font-bold uppercase tracking-wider">
                [ELEVATION // TOPOGRAPHY]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">OpenTopography & SRTM</h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr ? 'نماذج الارتفاعات الرقمية العالمية (DEM) لحساب الجريان الطوبولوجي.' : 'Global digital elevation models for hydrological slope and flood routing.'}
              </p>
            </div>

            <div className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-3">
              <div className="text-[11px] font-mono text-[#D48207] font-bold uppercase tracking-wider">
                [INTEROPERABILITY // OGC]
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">OGC Spatial Standards</h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr ? 'الامتثال الكامل لمعايير WFS و WMS و GeoTIFF لتكامل النظم العالمية.' : 'Strict compliance with WFS, WMS, and Cloud-Optimized GeoTIFF geospatial protocols.'}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* ── Section 6: Research Residency & Collaboration CTA ───────────────── */}
      <section className="py-24 bg-[#05070A]">
        <div className="w-full max-w-[1450px] mx-auto px-6 text-center">

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
              {isAr ? 'التعاون الأكاديمي والزمالة البحثية' : 'RESEARCH RESIDENCY & COLLABORATION'}
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-[#EDF0F1] tracking-tight">
              {isAr ? 'ساهم في صياغة الجيل القادم من الذكاء الاصطناعي الجغرافي' : 'Advance the Frontier of Spatial AI with VEXL'}
            </h2>

            <p className="text-base text-[#9AA5AC] leading-relaxed max-w-2xl mx-auto">
              {isAr
                ? 'نرحب بالباحثين، والأكاديميين، والمؤسسات الجغرافية للمشاركة في تطوير واختبار النماذج المكانية الرائدة وتطبيقات الأمان الطوبولوجي.'
                : 'We collaborate with academic institutions, PhD researchers, and geospatial agencies worldwide to pioneer foundation models, benchmark GeoAI reasoning, and deploy real-world planetary solutions.'}
            </p>

            <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/careers"
                className="px-8 py-3 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white text-sm font-semibold shadow-sm transition-all"
              >
                {isAr ? 'استكشف فرص الزمالة البحثية' : 'Explore Research Residency'}
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3 rounded-sm border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/50 text-[#EDF0F1] text-sm font-medium transition-all"
              >
                {isAr ? 'تواصل مع الفريق العلمي' : 'Contact Research Team'}
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* ── Paper Details & Abstract Modal ─────────────────────────────────── */}
      <AnimatePresence>
        {activePaperModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-3xl rounded-lg border border-[#262C31] bg-[#12161A] p-6 sm:p-8 shadow-2xl text-[#EDF0F1] my-8 max-h-[90vh] overflow-y-auto relative"
            >
              {/* Close button */}
              <button
                onClick={() => setActivePaperModal(null)}
                className="absolute top-6 right-6 p-2 rounded text-[#9AA5AC] hover:text-[#EDF0F1] hover:bg-[#171C21] transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>

              <div className="space-y-6">

                {/* Global Award Banner */}
                {activePaperModal.awardEn && (
                  <div className="p-3.5 rounded-sm bg-amber-500/10 border border-amber-500/30 flex items-center gap-3 text-[#D48207]">
                    <Award size={20} className="shrink-0" />
                    <div className="text-xs font-semibold">
                      {isAr ? activePaperModal.awardAr : activePaperModal.awardEn}
                    </div>
                  </div>
                )}

                {/* Meta Header */}
                <div className="flex flex-wrap items-center gap-3 pr-8">
                  <span className="px-3 py-1 rounded bg-[#0E93A3]/10 border border-[#0E93A3]/20 text-[#0E93A3] text-xs font-mono font-bold">
                    {isAr ? activePaperModal.categoryLabelAr : activePaperModal.categoryLabelEn}
                  </span>
                  <span className="text-xs font-mono text-[#9AA5AC]">{activePaperModal.date}</span>
                  <span className="text-xs font-mono text-[#9AA5AC]">License: {activePaperModal.license}</span>
                </div>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-bold text-[#EDF0F1] leading-snug">
                  {isAr ? activePaperModal.titleAr : activePaperModal.titleEn}
                </h3>

                {/* Authors & Institutional Affiliation */}
                <div className="text-xs font-mono text-[#9AA5AC] pt-2 border-t border-[#262C31] space-y-1">
                  <div>
                    <span className="text-[#626F79] font-bold uppercase tracking-wider">{isAr ? 'المؤلف الرئيسي:' : 'PRIMARY AUTHOR:'} </span>
                    <span className="text-[#EDF0F1] font-bold">{isAr ? activePaperModal.authorsAr : activePaperModal.authorsEn}</span>
                  </div>
                  <div className="text-[11px] text-[#9AA5AC]">
                    Philipps-Universität Marburg, Germany · Cairo University, Egypt · VEXL Research Labs
                  </div>
                </div>

                {/* Abstract */}
                <div className="space-y-2">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold">
                    {isAr ? 'المستخلص العلمي (Abstract)' : 'ABSTRACT'}
                  </h4>
                  <p className="text-sm text-[#9AA5AC] leading-relaxed p-4 rounded-sm bg-[#05070A] border border-[#262C31]">
                    {isAr ? activePaperModal.abstractAr : activePaperModal.abstractEn}
                  </p>
                </div>

                {/* Key Findings */}
                <div className="space-y-2">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold">
                    {isAr ? 'النتائج الرئيسية للمشروع' : 'KEY SCIENTIFIC FINDINGS'}
                  </h4>
                  <ul className="space-y-2 text-xs text-[#9AA5AC]">
                    {(isAr ? activePaperModal.keyFindingsAr : activePaperModal.keyFindingsEn).map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] mt-1.5 shrink-0" />
                        <span>{finding}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Modal Footer: BibTeX & Actions */}
                <div className="pt-6 border-t border-[#262C31] flex flex-wrap items-center justify-between gap-4">
                  <button
                    onClick={() => handleCopyBibtex(activePaperModal)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] text-xs font-mono text-[#EDF0F1] hover:border-[#0E93A3]/50 transition-all cursor-pointer"
                  >
                    {copiedBibtexId === activePaperModal.id ? (
                      <>
                        <Check size={14} className="text-[#0E93A3]" />
                        <span className="text-[#0E93A3] font-bold">{isAr ? 'تم نسخ BibTeX بنجاح!' : 'BibTeX Copied!'}</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        <span>{isAr ? 'نسخ استشهاد BibTeX' : 'Copy BibTeX Citation'}</span>
                      </>
                    )}
                  </button>

                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={activePaperModal.pdfUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs shadow-sm transition-all cursor-pointer"
                    >
                      <Download size={13} />
                      <span>{isAr ? 'تحميل البحث (PDF)' : 'Download Full Paper (PDF)'}</span>
                    </a>
                    <a
                      href={activePaperModal.researchGateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] text-xs text-[#EDF0F1] hover:text-[#0E93A3] hover:border-[#0E93A3]/30 transition-all font-mono"
                    >
                      <span>ResearchGate</span>
                      <ExternalLink size={12} />
                    </a>
                    <a
                      href={`https://doi.org/${activePaperModal.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-[#9AA5AC] hover:text-[#0E93A3] transition-colors font-mono"
                    >
                      <span>DOI</span>
                      <ExternalLink size={12} />
                    </a>
                    <button
                      onClick={() => setActivePaperModal(null)}
                      className="px-5 py-2.5 rounded-sm border border-[#262C31] bg-[#171C21] hover:border-[#0E93A3]/40 text-xs font-medium text-[#EDF0F1] cursor-pointer"
                    >
                      {isAr ? 'إغلاق' : 'Close'}
                    </button>
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ResearchOverview;
